import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import FormTextField from "../../../../../../views/components/common/form/FormTextField";
import InlineFormError from "./InlineFormError";
import {
  InventoryOpsRemovedQty,
  InventoryOpsReversalAction,
  InventoryOpsReversalStage,
} from "../../../../../../models/ProjectManagement/InventoryOpsRequests";

const MAX_REASON_LENGTH = 500;

export const REVERSAL_STAGE_LABELS: Record<InventoryOpsReversalStage, string> =
  {
    "raw-inward": "Raw Inward",
    "print-outward": "Print Outward",
    "print-inward": "Print Inward",
  };

const qtyWord = (stage: InventoryOpsReversalStage) =>
  stage === "print-outward" ? "sent" : "received";

/** "Removed 500 received (TS-S 500)" — shown inline after a successful undo / revert. */
export const formatRemovedQty = (
  stage: InventoryOpsReversalStage,
  action: InventoryOpsReversalAction,
  removed: InventoryOpsRemovedQty | null,
): string => {
  const verb = action === "undo-last" ? "Undid the last batch" : "Reverted";
  if (!removed) return `${verb} on ${REVERSAL_STAGE_LABELS[stage]}.`;

  const parts = [`${removed.quantity} ${qtyWord(stage)}`];
  if (removed.rejected_quantity > 0) {
    parts.push(`${removed.rejected_quantity} rejected`);
  }
  if (removed.passed_quantity > 0) {
    parts.push(`${removed.passed_quantity} passed`);
  }
  const skuLines = (removed.sku_variants || [])
    .filter((line) => line.quantity > 0)
    .map((line) => `${line.title || line.sku} ${line.quantity}`);

  return `${verb} on ${REVERSAL_STAGE_LABELS[stage]}: removed ${parts.join(", ")}${
    skuLines.length > 0 ? ` (${skuLines.join(", ")})` : ""
  }.`;
};

interface InventoryOpsReversalDialogProps {
  open: boolean;
  stage: InventoryOpsReversalStage;
  action: InventoryOpsReversalAction;
  /** Current received / sent qty on the stage. */
  currentQty: number;
  /** Inward counted at QC: undo / revert also removes its QC result. */
  countAtQc: boolean;
  onClose: () => void;
  /** Throws with the server message; the dialog shows it as-is. */
  onConfirm: (reason: string) => Promise<void>;
}

const InventoryOpsReversalDialog: React.FC<InventoryOpsReversalDialogProps> = ({
  open,
  stage,
  action,
  currentQty,
  countAtQc,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isUndo = action === "undo-last";
  const isInward = stage !== "print-outward";
  const label = REVERSAL_STAGE_LABELS[stage];

  useEffect(() => {
    if (!open) return;
    setReason("");
    setError("");
  }, [open, stage, action]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const trimmedReason = reason.trim();

  const handleConfirm = async () => {
    if (submitting) return;
    if (!trimmedReason) {
      setError(
        `Enter a reason to ${isUndo ? "undo the last batch" : "revert"}.`,
      );
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await onConfirm(trimmedReason);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong, please try again later.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const whatIsRemoved = isUndo
    ? stage === "print-outward"
      ? "the most recent batch sent to the printer"
      : countAtQc
        ? "the most recent count-at-QC batch: its received qty and its QC result (tested, rejected and passed qty)"
        : "the most recent qty save"
    : `all qty: ${qtyWord(stage)} ${currentQty} goes to 0`;

  const notes: string[] = [];
  if (isUndo) {
    notes.push(
      "You can undo again to remove the batch before it. Batches saved before undo was available can only be reverted.",
    );
  } else if (isInward) {
    notes.push("QC goes back to Not started and its QC images are hidden.");
    if (countAtQc) {
      notes.push(
        "The count-at-QC result is removed too. You can count again at QC, or switch to the qty flow.",
      );
    }
  }
  notes.push(
    "The record stays active. Received status, warehouse, printer, timeline and invoice are kept.",
    "To undo stock that has moved on, work backwards: print inward → print outward → raw inward.",
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
          borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
        }}
      >
        <CustomTypography variant="heading4" fontWeight={700}>
          {isUndo ? `Undo last batch — ${label}` : `Revert ${label}`}
        </CustomTypography>
        <IconButton
          onClick={handleClose}
          disabled={submitting}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, minHeight: 0, overflowY: "auto" }}>
        <CustomTypography variant="body2" fontWeight={600}>
          This removes {whatIsRemoved}.
        </CustomTypography>
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
          {notes.map((note) => (
            <li key={note}>
              <CustomTypography variant="body3" color="text.secondary">
                {note}
              </CustomTypography>
            </li>
          ))}
        </Box>

        <CustomTypography
          variant="heading8"
          color={theme.palette.customColors.greyTextColor}
          style={{ marginTop: 16, marginBottom: 8 }}
        >
          Reason *
        </CustomTypography>
        <FormTextField
          id="inventory_ops_reversal_reason"
          multiline
          minRows={3}
          maxLength={MAX_REASON_LENGTH}
          borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
          value={reason}
          placeholder="e.g. Entered 500 instead of 50"
          disabled={submitting}
          onChange={(_, value) => {
            setReason((value || "").slice(0, MAX_REASON_LENGTH));
            if (error && (value || "").trim()) setError("");
          }}
          sx={{ marginBottom: "0px" }}
        />
        <CustomTypography
          variant="body3"
          color="text.secondary"
          sx={{ display: "block", textAlign: "right", mt: 0.5 }}
        >
          {reason.length}/{MAX_REASON_LENGTH}
        </CustomTypography>
        <InlineFormError message={error} />
      </DialogContent>

      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          flexShrink: 0,
          borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
        }}
      >
        <AppButton
          label="Cancel"
          variant="grayscale"
          onClick={handleClose}
          disabled={submitting}
        />
        <AppButton
          label={
            submitting
              ? isUndo
                ? "Undoing…"
                : "Reverting…"
              : isUndo
                ? "Undo last batch"
                : "Revert all qty"
          }
          variant="primary"
          onClick={() => {
            void handleConfirm();
          }}
          disabled={submitting || !trimmedReason}
        />
      </Box>
    </Dialog>
  );
};

export default InventoryOpsReversalDialog;
